import { useState } from "react"
import { Alert, Button, Dialog, Loader, Stack, Typography } from "@octopus-energy/coral/atoms"
import { FormikCheckbox } from "@octopus-energy/coral/formik"
import { ZodFormik } from "@oefr/ui"
import { useTranslations } from "next-intl"
import z from "zod"
import { FormActions } from "@/components/organisms"
import { useEnrollmentEcoSession } from "@/features/ecoSession/hooks/useEnrollmentEcoSession"
import { type FragmentOf, graphql, readFragment } from "@/graphql"
import { useToast } from "@/hooks/useToast"
import { useUpdateIndustryConsentMutation } from "../../hooks/useUpdateIndustryConsentMutation"
import { type CommonAgreementConsentsProps } from "../../types/consentProps"

const graphQLFragments = {
    agreement: graphql(`
        fragment ElectricityAgreementConsents_Agreement on AgreementType {
            consents {
                collectHalfHourlyConsumptionData
                collectDailyConsumptionData
            }
            supplyPoint {
                id
                externalIdentifier
                meterPoint {
                    address {
                        fullAddress
                    }
                }
            }
        }
    `),
}

const ElectricityAgreementConsentsSchema = z.object({
    collectHalfHourlyConsumptionData: z.boolean(),
    collectDailyConsumptionData: z.boolean(),
})

type ElectricityAgreementConsentsProps = CommonAgreementConsentsProps & {
    agreement: FragmentOf<typeof graphQLFragments.agreement>
    accountNumber?: string
    isEcoSessionActive?: boolean
}

export function ElectricityAgreementConsents(props: ElectricityAgreementConsentsProps) {
    const t = useTranslations("ElectricityAgreementConsents")

    const [open, setOpen] = useState(false)
    const [pendingValues, setPendingValues] = useState<z.infer<
        typeof ElectricityAgreementConsentsSchema
    > | null>(null)

    const { unenroll } = useEnrollmentEcoSession()

    const updateIndustryConsent = useUpdateIndustryConsentMutation()
    const toast = useToast()

    const agreement = readFragment(graphQLFragments.agreement, props.agreement)

    const collectDailyConsumptionDataConsent = Boolean(
        agreement?.consents?.collectDailyConsumptionData
    )
    const collectHalfHourlyConsumptionDataConsent = Boolean(
        agreement?.consents?.collectHalfHourlyConsumptionData
    )

    const areAllConsentsGiven = Boolean(
        collectHalfHourlyConsumptionDataConsent && collectDailyConsumptionDataConsent
    )

    const updateConsents = async (
        consentData: z.infer<typeof ElectricityAgreementConsentsSchema>
    ) => {
        if (!agreement?.supplyPoint?.id) {
            return
        }

        await updateIndustryConsent.mutateAsync(
            {
                supplyPointId: agreement.supplyPoint.id,
                consentData,
            },
            {
                onSuccess: () => {
                    toast("success.update", { type: "success" })
                    props?.onCompleted?.()
                },
            }
        )
    }

    const handleUnenroll = () => {
        unenroll.mutate({
            accountNumber: props.accountNumber ?? "",
            prmId: agreement?.supplyPoint?.externalIdentifier ?? "",
            prmAddress: agreement?.supplyPoint?.meterPoint?.address?.fullAddress ?? "",
        })
    }

    const onSubmit = async (consentData: z.infer<typeof ElectricityAgreementConsentsSchema>) => {
        const isDisablingTracking =
            collectHalfHourlyConsumptionDataConsent === true &&
            consentData.collectHalfHourlyConsumptionData === false

        if (!open && isDisablingTracking && props.isEcoSessionActive) {
            setPendingValues(consentData)

            setOpen(true)

            return
        }

        await updateConsents(consentData)
    }

    return (
        <>
            <Dialog
                ariaLabelledBy="eco-session-warning"
                open={open}
                onClose={() => setOpen(false)}
            >
                <Stack
                    flexDirection="column"
                    gap="md"
                >
                    <Typography
                        textStyle="h3"
                        id="eco-session-warning"
                    >
                        {t("dialog.title")}
                    </Typography>
                    <Typography>{t("dialog.content")}</Typography>
                    <Stack
                        gap="sm"
                        flexDirection="column"
                    >
                        <Button
                            onClick={async () => {
                                if (pendingValues) {
                                    handleUnenroll()

                                    await updateConsents(pendingValues)
                                }

                                setOpen(false)
                            }}
                            loading={updateIndustryConsent.isPending}
                            loadingLabel={<Loader size={20} />}
                        >
                            {t("dialog.continue")}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => setOpen(false)}
                        >
                            {t("dialog.cancel")}
                        </Button>
                    </Stack>
                </Stack>
            </Dialog>
            <Stack
                flexDirection="column"
                gap="md"
            >
                {!areAllConsentsGiven && (
                    <Alert severity="warning">
                        <Typography color="inherit">{t("warning")}</Typography>
                    </Alert>
                )}
                <ZodFormik
                    initialValues={{
                        collectHalfHourlyConsumptionData: Boolean(
                            agreement?.consents?.collectHalfHourlyConsumptionData
                        ),
                        collectDailyConsumptionData: Boolean(
                            agreement?.consents?.collectDailyConsumptionData
                        ),
                    }}
                    validationSchema={ElectricityAgreementConsentsSchema}
                    onSubmit={onSubmit}
                    enableReinitialize
                >
                    <Stack
                        flexDirection="column"
                        gap="md"
                    >
                        <Typography textStyle="body2">{t("authorization")}</Typography>
                        <Stack
                            gap="xxs"
                            justifyContent="space-between"
                        >
                            <FormikCheckbox
                                name={"collectHalfHourlyConsumptionData"}
                                disabled={props.disabled}
                                label={t("halfHourlyConsumptionData")}
                            />
                        </Stack>
                        <Stack
                            gap="xxs"
                            justifyContent="space-between"
                        >
                            <FormikCheckbox
                                name={"collectDailyConsumptionData"}
                                disabled={props.disabled}
                                label={t("dailyConsumptionData")}
                            />
                        </Stack>
                        {!props.disabled && (
                            <FormActions>
                                <FormActions.Error error={updateIndustryConsent.error} />
                                <FormActions.Error error={unenroll.error} />
                                <FormActions.Accept
                                    size="small"
                                    color="primary500"
                                />
                            </FormActions>
                        )}
                    </Stack>
                </ZodFormik>
            </Stack>
        </>
    )
}

ElectricityAgreementConsents.messages = [
    "ElectricityAgreementConsents",
    ...useToast.messages,
    ...FormActions.Error.messages,
] as const

ElectricityAgreementConsents.fragments = graphQLFragments
